import axios from 'axios';

// Default to a relative path so the bundle works on any host that serves it.
// An absolute default breaks the HTTPS deployment (mixed content) and skips
// the reverse proxy's authentication.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  // Send the httpOnly session cookie with every request.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// A 401 anywhere means the session expired or was never established. Tell the
// app rather than letting individual pages render a confusing error.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401 &&
        !String(error.config?.url || '').includes('/auth/')) {
      window.dispatchEvent(new CustomEvent('affiwise:signed-out'));
    }
    return Promise.reject(error);
  }
);

export const dashboardApi = {
  // Auth
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),

  // Dashboard stats
  getStats: () => api.get('/stats'),

  // Influencers
  getInfluencers: (params) => api.get('/influencers', { params }),
  getInfluencer: (id) => api.get(`/influencers/${id}`),
  updateInfluencerStatus: (id, data) => api.put(`/influencers/${id}/status`, data),
  addNote: (id, data) => api.post(`/influencers/${id}/notes`, data),

  // Search
  search: (query) => api.get('/search', { params: { q: query } }),

  // Export
  exportCSV: () => api.get('/export', { responseType: 'blob' }),

  // Scraper control (Apify)
  startScraper: (flow = 'scraper-only') => api.post('/scraper/start', { flow }),
  getScraperStatus: () => api.get('/scraper/status'),

  // Heepsy scraper control
  startHeepsyScraper: (startPage = 1) => api.post('/heepsy/scrape/start', { startPage }),
  getHeepsyStatus: (sessionId) => api.get(`/heepsy/scrape/status/${sessionId}`),
  stopHeepsyScraper: (sessionId) => api.post(`/heepsy/scrape/stop/${sessionId}`),
  getHeepsySessions: (limit = 10) => api.get('/heepsy/sessions', { params: { limit } }),

  // Outreach campaigns
  getCampaigns: () => api.get('/outreach/campaigns'),
  createCampaign: (data) => api.post('/outreach/campaign', data),
  getCampaign: (id) => api.get(`/outreach/campaign/${id}`),
  getCampaignStats: (id) => api.get(`/outreach/campaign/${id}/stats`),

  // CRM — conversation threads, review queue, manual overrides (Module 6)
  getThread: (id) => api.get(`/crm/influencers/${id}/thread`),
  getReviewQueue: (limit = 50) => api.get('/crm/review-queue', { params: { limit } }),
  getCrmStats: () => api.get('/crm/stats'),
  getDeliverability: (days = 1) => api.get('/crm/deliverability', { params: { days } }),
  overrideClassification: (id, data) => api.post(`/crm/classifications/${id}/override`, data),
  approveDraft: (conversationId, data) => api.post(`/crm/drafts/${conversationId}/approve`, data),
  rejectDraft: (conversationId, data) => api.post(`/crm/drafts/${conversationId}/reject`, data),
  sendManualReply: (id, data) => api.post(`/crm/influencers/${id}/reply`, data),
  suppressInfluencer: (id, data) => api.post(`/crm/influencers/${id}/suppress`, data),

  // Admin panel — campaigns, runtime settings, pipeline, audit
  getAdminSettings: () => api.get('/admin/settings'),
  updateAdminSetting: (key, value) => api.put(`/admin/settings/${key}`, { value }),
  resetAdminSetting: (key) => api.delete(`/admin/settings/${key}`),
  previewTargeting: (data) => api.post('/admin/settings/preview-targeting', data),
  getCampaigns2: () => api.get('/admin/campaigns'),
  syncCampaigns: () => api.post('/admin/campaigns/sync'),
  updateCampaign: (id, data) => api.patch(`/admin/campaigns/${id}`, data),
  campaignEligible: (id) => api.get(`/admin/campaigns/${id}/eligible`),
  pushCampaign: (id, data) => api.post(`/admin/campaigns/${id}/push`, data),
  getPipeline: () => api.get('/admin/pipeline'),
  getAudit: (limit = 100) => api.get('/admin/audit', { params: { limit } }),

  // Settings
  getSettings: () => api.get('/settings'),
  updateSettings: (settings) => api.post('/settings', settings),
  updateSetting: (key, value) => api.put(`/settings/${key}`, { value }),
};

export default api;
